// F-3 · §6 compliance layer — buildComplianceSteps (blueprint v2 §6, task F-3).
// PURE function: deterministic, no React imports, never mutates inputs.
//
// Step shape per §6: {ref, requirement, status: pending|documented|na, evidence?,
// by?, at?, fields?}. Cites specific standard refs everywhere (§6.3).
import type {
  ClassificationResult,
  ComplianceStep,
  ConstructionClass,
  LiftDetail,
} from '@/types/planData';

export interface ComplianceGate {
  cls: ConstructionClass;
  qeFieldsRequired: boolean;
  personnel: string[];
}

// Z19 (§8.1/Q12): personnel lifting is ADVISORY-ONLY — never a first-class
// force scenario. The §9.6 + §10.2 compliance steps stay.
export const PERSONNEL_LIFT_BANNER =
  'Advisory (§8.1): Tower-owner policy may prohibit personnel lifting — confirm before planning. ' +
  'Personnel are raised by crane or aerial lift where required.';

export const WLL_50_PERCENT_NOTE =
  '§10.2: When lifting personnel, the WLL of the rigging used for personnel lifting is reduced by fifty percent (50%).';

/** Class IV sign-off is GATED on the QE fields (name/firm/report) being present first. */
export function QE_FIELDS_REQUIRED(cls: ConstructionClass | null): boolean {
  return cls === 'IV';
}

const PROOF_TEST_FIELDS: Record<string, unknown> = {
  // Full VERIFIED a)–i) checklist (D7 / blueprint §6 item 1):
  a_testLoad: 'Test load pre-filled at 1.25 × max anticipated lifted load unless QE approves otherwise',
  b_noOverload: 'Test must not overload the structure or anchorages or exceed any component WLL',
  c_noPersonnel: 'No personnel on the structure during the test (attestation)',
  d_loadPlacement: 'Proof load placed at the calculated distance from base establishing the max planned load angle per the plan',
  e_holdMinutes: 5,
  f_maxHeightFt: 5,
  g_loadCellCert: '',
  h_postTestInspection: '',
  i_reTestOnAlteration: 'QP evaluates the need for a re-test on ANY rigging-configuration alteration',
};

export function buildComplianceSteps(
  classification: ClassificationResult | null,
  liftDetails: LiftDetail[],
  extra?: { jhaReference?: string | null },
): ComplianceStep[] {
  const steps: ComplianceStep[] = [];
  const cls: ConstructionClass | null = classification?.selected ?? null;
  const anyPersonnel = liftDetails.some((l) => l.personnelLift);
  const anyS3 = liftDetails.some((l) => l.scenario === 'S3-jump');
  const exceptionInvoked = classification?.exception?.invoked != null;
  const qeEngaged = classification?.exception?.qeEngaged === true || cls === 'IV';

  // Rule 1 — §9.6 proof test (Z5: trigger = exception invoked OR personnelLift)
  if (exceptionInvoked || anyPersonnel) {
    steps.push({
      ref: 'A10.48-2023 §9.6',
      requirement:
        'Documented proof test per §9.6 completed prior to active hoisting operations — full a)–i) checklist below; log each test in the Proof Test Log (one record per rigging configuration/location).',
      status: 'pending',
      fields: PROOF_TEST_FIELDS,
    });
  }

  // Rule 2 — QE engagement (TIA-322-A)
  if (qeEngaged) {
    steps.push({
      ref: 'ANSI/TIA-322-A',
      requirement: 'Qualified Engineer engaged per ANSI/TIA-322-A — evaluation report attached before sign-off.',
      status: 'pending',
      fields: {
        qeName: '',
        qeFirm: '',
        evaluationReport: '',
      },
    });
  }

  // Rule 3 — §4.4.5 plan-considerations checklist. G-3 RECONCILED (2026-09-07):
  // items now cite §4.4.5.1 General (a)-p) verbatim structure from the standard
  // full text — no longer a secondary-source list.
  steps.push({
    ref: 'A10.48-2023 §4.4.5.1',
    requirement:
      'Plan-considerations checklist (§4.4.5.1 General, a-p): site information; location of supporting structure; ' +
      'scope of work; construction classification; project personnel; construction sequence and duration; ' +
      'construction equipment incl. planned hoisting system(s); operational and non-operational construction loads incl. limitations; ' +
      'special procedures/details for a safe work environment; type of climbing facilities; supporting structure type, size, and details ' +
      'needed to identify applicable rigging attachment points incl. existing obstructions; rigging attachment details and location(s); ' +
      'gross loads to be lifted; general site layout identifying hoist and tag locations, staging areas, and site-specific obstructions; ' +
      'proof testing / load testing / specific procedures for removal or disconnection of in-service structural members incl. guy assemblies, ' +
      'critical procedural operations, temporary bracing or shoring, and/or field monitoring requirements; antennas or structure portions that ' +
      'may radiate RF energy impacting rigging equipment incl. arc flash and touch point issues (QP shall have RF experience and engage a ' +
      'qualified engineer when required).',
    status: 'pending',
    fields: {
      footnote: 'Per ANSI/ASSP A10.48-2023 §4.4.5.1 (verified against the standard full text). Additional subsections apply: §4.4.5.2 lifted loads, §4.4.5.3 gin poles, §4.4.5.4 hoists, §4.4.5.5 rigging blocks, §4.4.5.6 load lines, §4.4.5.7 tag lines, §4.4.5.8 detached lifting devices, §4.4.5.9 guy wire work.',
      structureTypeSize: '',
      attachmentPoints: '',
      siteLayout: '',
      obstructions: '',
    },
  });

  // Rule 4 — RF hazard assessment (§7): advisory when crossing/jump lines (S3)
  if (anyS3) {
    steps.push({
      ref: 'A10.48-2023 §7 (advisory)',
      requirement:
        'RF hazard assessment advised — jump/crossing lines or work near radiating appurtenances. Confirm RF exposure limits before hoisting.',
      status: 'pending',
    });
  }

  // Rule 5 — JHA linkage (placeholder when none linked)
  if (extra?.jhaReference) {
    steps.push({
      ref: 'A10.48-2023 §1.6 rule 3 (JHA)',
      requirement: `Project JHA linked: ${extra.jhaReference}. Confirm plan hazards are addressed in the JHA before work begins.`,
      status: 'pending',
    });
  } else {
    steps.push({
      ref: 'A10.48-2023 §1.6 rule 3 (JHA)',
      requirement: 'No JHA linked (placeholder step) — link the project JHA from the plan form so hazards carry through.',
      status: 'pending',
    });
  }

  // Rule 6 — sign-off gate (class-dependent personnel; Class IV gated on QE fields)
  steps.push({
    ref: 'A10.48-2023 sign-off gate',
    requirement:
      cls === 'IV'
        ? 'Class IV sign-off GATED: the QE name, firm, and evaluation-report fields must be completed BEFORE signing. Personnel: qualified person, onsite competent person(s), qualified engineer.'
        : `Sign-off by ${classification?.requiredPersonnel?.join(', ') ?? 'qualified person + onsite competent person(s)'}.`,
    status: 'pending',
    fields: {
      qeFieldsRequired: QE_FIELDS_REQUIRED(cls),
      personnel: classification?.requiredPersonnel ?? [],
    },
  });

  // Rule 7 — §10.2 50%-WLL note (personnel lifts only, VERIFIED)
  if (anyPersonnel) {
    steps.push({
      ref: 'A10.48-2023 §10.2',
      requirement: WLL_50_PERCENT_NOTE,
      status: 'pending',
    });
  }

  return steps;
}
